import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { subscribeToNewsletter } from '../services/newsletterService';
import RecaptchaComponent from './RecaptchaComponent';

const NewsletterDialog = ({ show, onHide }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { email: settingsEmail, updateEmail, updateSubscription } = useSettings();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [recaptchaError, setRecaptchaError] = useState('');
  const recaptchaRef = useRef(null);

  // Pre-fill email from settings when dialog opens
  useEffect(() => {
    if (show && settingsEmail) {
      setEmail(settingsEmail);
    }
  }, [show, settingsEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setRecaptchaError('');

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ type: 'danger', text: t('newsletter.invalidEmail') });
      return;
    }

    setLoading(true);
    try {
      // Execute reCAPTCHA verification
      let recaptchaToken = null;
      if (recaptchaRef.current && recaptchaRef.current.executeRecaptcha) {
        recaptchaToken = await recaptchaRef.current.executeRecaptcha();
        if (!recaptchaToken) {
          setMessage({ type: 'danger', text: t('errors.recaptchaVerificationFailed') });
          setLoading(false);
          return;
        }
      }

      const result = await subscribeToNewsletter(email, 'home', 'v1.0', recaptchaToken);
      if (result.success) {
        // Update settings with email and subscription status
        updateEmail(email);
        updateSubscription(true);
        
        setMessage({ type: 'success', text: result.message || t('newsletter.subscribeSuccess') });
        setEmail('');
        // Auto-close after 2 seconds on success
        setTimeout(() => {
          onHide();
          setMessage({ type: '', text: '' });
        }, 2000);
      } else {
        setMessage({ type: 'danger', text: result.message || t('newsletter.subscribeError') });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setMessage({ type: 'danger', text: t('newsletter.subscribeError') });
    } finally {
      setLoading(false);
    }
  };

  const handleRecaptchaVerify = () => {
    setRecaptchaError('');
    // Clear any existing error when reCAPTCHA is verified
    if (message.text && message.text.includes('reCAPTCHA')) {
      setMessage({ type: '', text: '' });
    }
  };

  const handleRecaptchaError = (error) => {
    setRecaptchaError(error);
  };

  const handleClose = () => {
    setEmail('');
    setMessage({ type: '', text: '' });
    onHide();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      centered
      contentClassName={theme === 'dark' ? 'bg-dark text-light' : ''}
    >
      <Modal.Header 
        closeButton
        closeVariant={theme === 'dark' ? 'white' : undefined}
        style={{
          backgroundColor: theme === 'dark' ? 'var(--card-bg)' : undefined,
          borderColor: 'var(--border-color)',
          color: 'var(--text-color)'
        }}
      >
        <Modal.Title style={{ color: 'var(--text-color)' }}>
          {t('newsletter.subscribeTitle')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{
        backgroundColor: theme === 'dark' ? 'var(--card-bg)' : undefined,
        color: 'var(--text-color)'
      }}>
        <p style={{ color: 'var(--text-color)' }}>
          {t('newsletter.subscribeDescription')}
        </p>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label style={{ color: 'var(--text-color)' }}>
              {t('newsletter.emailLabel')}
            </Form.Label>
            <Form.Control
              type="email"
              placeholder={t('newsletter.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
              autoFocus
              style={{
                backgroundColor: theme === 'dark' ? 'var(--card-bg)' : undefined,
                color: 'var(--text-color)',
                borderColor: 'var(--border-color)'
              }}
            />
          </Form.Group>
          {message.text && (
            <Alert variant={message.type} className="mb-3">
              {message.text}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <RecaptchaComponent
              ref={recaptchaRef}
              onVerify={handleRecaptchaVerify}
              onError={handleRecaptchaError}
              siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            />
            {recaptchaError && (
              <div className="text-danger small mt-1">
                {recaptchaError}
              </div>
            )}
          </Form.Group>
          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={loading}>
              {t('newsletter.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? t('newsletter.subscribing') : t('newsletter.subscribe')}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default NewsletterDialog;
